import {
  createMuiTheme,
  responsiveFontSizes,
  ThemeOptions,
} from "@material-ui/core/styles";
import { TypographyUtils } from "@material-ui/core/styles/createTypography";
import { Shadows } from "@material-ui/core/styles/shadows";

import settings from "../styles/settings/settings.module.scss";

import { convertLetterSpacingToRem } from "./utils";

type ThemeOptionsWithUtils = ThemeOptions & {
  typography: ThemeOptions["typography"] & TypographyUtils;
};

const options: ThemeOptionsWithUtils = {
  breakpoints: {
    values: {
      xs: parseInt(settings.tokenBreakpointXs),
      sm: parseInt(settings.tokenBreakpointSm),
      md: parseInt(settings.tokenBreakpointMd),
      lg: parseInt(settings.tokenBreakpointLg),
      xl: parseInt(settings.tokenBreakpointXl),
    },
  },
  shadows: Array<string>(25).fill("none") as Shadows,
  palette: {
    primary: {
      main: settings.colorYellow,
    },
    secondary: {
      main: settings.colorTurquoise,
    },
    text: {
      primary: settings.colorYellowContrast,
      secondary: settings.colorYellowContrast,
    },
    error: {
      main: settings.tokenColorError,
    },
  },
  spacing: parseInt(settings.layoutSpacingBase),
  typography: {
    pxToRem: (px: number): string =>
      `${px / parseInt(settings.tokenFontSizeMain)}rem`,
    fontFamily: settings.tokenFontMain,
    fontSize: parseInt(settings.tokenFontSizeMain),
    button: {
      fontWeight: parseInt(settings.tokenFontWeightBold),
      lineHeight: "20px",
      letterSpacing: "1px",
    },
    h1: {
      fontSize: parseInt(settings.tokenFontSizeHeadingExtraLarge),
      lineHeight: convertLetterSpacingToRem(
        parseInt(settings.tokenLetterSpacingHeadingExtraLarge),
        parseInt(settings.tokenFontSizeHeadingExtraLarge)
      ),
      fontWeight: parseInt(settings.tokenFontWeightBold),
    },
    h2: {
      fontSize: parseInt(settings.tokenFontSizeHeadingLarge),
      lineHeight: convertLetterSpacingToRem(
        parseInt(settings.tokenLetterSpacingHeadingLarge),
        parseInt(settings.tokenFontSizeHeadingLarge)
      ),
      fontWeight: parseInt(settings.tokenFontWeightBold),
    },
    h3: {
      fontSize: parseInt(settings.tokenFontSizeHeadingNormal),
      lineHeight: convertLetterSpacingToRem(
        parseInt(settings.tokenLetterSpacingHeadingNormal),
        parseInt(settings.tokenFontSizeHeadingNormal)
      ),
      fontWeight: parseInt(settings.tokenFontWeightBold),
    },
  },
  overrides: {
    MuiPopover: {
      root: {
        zIndex: parseInt(settings.tokenZIndex5),
      },
    },
    MuiAppBar: {
      positionFixed: {
        zIndex: parseInt(settings.tokenZIndex4),
      },
    },
    MuiAutocomplete: {
      inputRoot: {
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
      },
      popper: {
        zIndex: parseInt(settings.tokenZIndex5),
        border: `1px solid ${settings.tokenColorInactiveEl}`,
        borderRadius: settings.tokenFormInputBorderRadius,
        backgroundColor: settings.colorWhite,
      },
    },
    MuiPaper: {
      root: {
        zIndex: parseInt(settings.tokenZIndex5),
        color: settings.tokenColorMainText,
      },
    },
    MuiSelect: {
      root: {
        display: "flex",
        alignItems: "center",
      },
      selectMenu: {
        height: "50px",
      },
    },
    MuiFormLabel: {
      root: {
        width: "100%",
        lineHeight: convertLetterSpacingToRem(
          parseInt(settings.tokenLetterSpacingHeadingNormal),
          parseInt(settings.tokenFontSizeMain)
        ),

        "&$focused": {
          color: settings.tokenColorMainText,
        },

        "&:hover + .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
          borderColor: settings.tokenColorHoveredEl,
        },
        "&:hover + .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: settings.tokenColorDisabledEl,
        },
        "&:hover&$focused + .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
          borderColor: settings.tokenColorActiveEl,
        },
      },
      asterisk: {
        color: settings.tokenColorError,
      },
    },
    MuiFormHelperText: {
      root: {
        marginLeft: "0",
      },
      contained: {
        marginLeft: "0",
      },
    },
    MuiList: {
      root: {
        border: `2px solid ${settings.colorYellow}`,
      },
    },
    MuiListItem: {
      root: {
        color: settings.tokenColorMainText,
      },
    },
    MuiInput: {
      formControl: {
        marginTop: "0 !important",
      },
    },
    MuiInputBase: {
      root: {
        color: settings.tokenColorMainText,
        minHeight: settings.tokenFormInputHeight,

        "&$error": {
          color: settings.tokenColorError,
          borderColor: settings.tokenColorError,
          backgroundColor: settings.colorProvincialPink,
        },
      },
      input: {
        boxSizing: "border-box",
      },
    },
    MuiOutlinedInput: {
      root: {
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: settings.tokenColorHoveredEl,
        },
        "&:hover&$focused .MuiOutlinedInput-notchedOutline": {
          borderColor: settings.tokenColorActiveEl,
        },
        $disabled: {
          borderColor: "red",
        },
        "&:hover&$disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: settings.tokenColorDisabledEl,
        },
      },
      input: {
        cursor: "text",
        padding: `0 ${parseInt(
          settings.layoutSpacingInnerHorizontalInputEl
        )}px`,
      },
      notchedOutline: {
        borderColor: settings.tokenColorInactiveEl,
      },
    },
    MuiInputLabel: {
      root: {
        paddingBottom: parseInt(settings.layoutSpacingBase) * 2,

        "&$error": {
          color: settings.tokenColorMainText,
        },
      },
      asterisk: {
        color: settings.tokenColorError,
      },
      outlined: {
        pointerEvents: "auto",
        position: "relative",
        paddingTop: 0,
      },
      formControl: {
        position: "relative",
      },
      shrink: {
        transform: "none !important",
      },
    },
    MuiCheckbox: {
      colorSecondary: {
        color: settings.tokenColorInactiveEl,
        "&$checked": {
          color: settings.colorYellow,
        },
      },
    },
    MuiRadio: {
      colorSecondary: {
        color: settings.tokenColorInactiveEl,
        "&$checked": {
          color: settings.colorYellow,
        },
      },
    },
    MuiButton: {
      root: {
        minHeight: "50px",
        padding: "15px 30px",
        color: settings.colorMineShaft,
        borderRadius: "50px",

        "&:before": {
          content: "''",
          position: "absolute",
          zIndex: 1,
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          borderRadius: "50px",
          transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
          //JIRA LPPLWEBMOB-583
          //background: `linear-gradient(90deg, ${settings.colorSilver} 0%, ${settings.colorDustyGray} 100%);`,
        },
      },
      label: {
        zIndex: 2,
      },
      containedSecondary: {
        background: settings.colorTurquoise,
        color: settings.colorTurquoiseContrast,
      },
      contained: {
        "&$disabled": {
          color: settings.colorWhite,

          "&:before": {
            opacity: 1,
          },
        },
      },
      containedPrimary: {
        "&:hover": {
          backgroundColor: settings.colorYellowDark,
          color: settings.colorYellowContrast,
        },
      },
      outlinedSecondary: {
        borderColor: settings.colorNavy,
        color: settings.colorNavy,
        "&:hover": {
          color: settings.colorNavy,
          borderColor: settings.colorNavy,
          backgroundColor: "transparent",
        },
      },
    },
    MuiSnackbarContent: {
      root: {
        backgroundColor: settings.colorYellow,
        color: settings.colorYellowContrast,
        paddingRight: parseInt(settings.layoutSpacingBase) * 8,
      },
    },
    MuiDialogActions: {
      root: {
        justifyContent: "center",
      },
    },
  },
};

export const theme = responsiveFontSizes(createMuiTheme(options));
